import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { permissionService } from "../services/service";

const PERMISSIONS_KEY = ["permissions"];

export const usePermissions = () => {
    const queryClient = useQueryClient();

    const [searchTerm, setSearchTerm] = useState("");
    const [moduleFilter, setModuleFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const params = useMemo(
        () => ({
            page: currentPage,
            per_page: pageSize,
            search: searchTerm || undefined,
            module: moduleFilter !== "all" ? moduleFilter : undefined,
            status: statusFilter !== "all" ? statusFilter : undefined,
        }),
        [currentPage, pageSize, searchTerm, moduleFilter, statusFilter]
    );

    const permissionsQuery = useQuery({
        queryKey: [...PERMISSIONS_KEY, "list", params],
        queryFn: () => permissionService.getAll(params),
        staleTime: 30000,
        retry: 1,
        retryDelay: 1000,
        keepPreviousData: true,
    });

    const [loadGrouped, setLoadGrouped] = useState(false);
    const groupedQuery = useQuery({
        queryKey: [...PERMISSIONS_KEY, "grouped"],
        queryFn: () => permissionService.getGrouped(),
        staleTime: 30000,
        enabled: loadGrouped,
        retry: 1,
        retryDelay: 1000,
    });

    const [loadModules, setLoadModules] = useState(false);
    const modulesQuery = useQuery({
        queryKey: [...PERMISSIONS_KEY, "modules"],
        queryFn: () => permissionService.getModules(),
        staleTime: 30000,
        enabled: loadModules,
        retry: 1,
        retryDelay: 1000,
    });

    const invalidate = useCallback(
        () => queryClient.invalidateQueries({ queryKey: PERMISSIONS_KEY }),
        [queryClient]
    );

    const fetchGroupedPermissions = useCallback(() => {
        setLoadGrouped(true);
        return groupedQuery.refetch();
    }, [groupedQuery]);

    const fetchModules = useCallback(() => {
        setLoadModules(true);
        return modulesQuery.refetch();
    }, [modulesQuery]);

    const pagination = permissionsQuery.data?.data || null;
    const permissions = pagination?.data || [];

    const assignPermission = useCallback(
        async (data) => {
            const response = await permissionService.assign(data);
            await invalidate();
            return response;
        },
        [invalidate]
    );

    const revokePermission = useCallback(
        async (data) => {
            const response = await permissionService.revoke(data);
            await invalidate();
            return response;
        },
        [invalidate]
    );

    const syncPermissions = useCallback(
        async (userlogId, permissionIds) => {
            const response = await permissionService.sync(userlogId, permissionIds);
            await invalidate();
            return response;
        },
        [invalidate]
    );

    const getPermissionById = useCallback((id) => permissionService.getById(id), []);

    const getUsersWithPermissions = useCallback(
        (p = {}) => permissionService.getUsersWithPermissions(p),
        []
    );

    const getUserPermissions = useCallback(
        (userlogId) => permissionService.getUserPermissions(userlogId),
        []
    );

    const getAvailablePermissions = useCallback(
        () => permissionService.getAvailable(),
        []
    );

    const checkPermission = useCallback((data) => permissionService.check(data), []);

    const isRateLimited = permissionsQuery.error?.response?.status === 429;

    return {
        permissions,
        pagination,
        groupedPermissions:
            groupedQuery.data?.data?.grouped_permissions ??
            groupedQuery.data?.data ??
            {},
        modules:
            groupedQuery.data?.data?.modules ??
            modulesQuery.data?.data ??
            [],

        loading: permissionsQuery.isLoading || permissionsQuery.isFetching,
        groupedLoading: groupedQuery.isLoading || groupedQuery.isFetching,
        modulesLoading: modulesQuery.isLoading || modulesQuery.isFetching,
        error: permissionsQuery.error,
        isRateLimited,

        searchTerm,
        setSearchTerm,
        moduleFilter,
        setModuleFilter,
        statusFilter,
        setStatusFilter,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,

        refresh: permissionsQuery.refetch,
        fetchPermissions: permissionsQuery.refetch,
        fetchGroupedPermissions,
        fetchModules,
        getPermissionById,
        getUsersWithPermissions,
        getUserPermissions,
        getAvailablePermissions,
        checkPermission,
        assignPermission,
        revokePermission,
        syncPermissions,
    };
};